import { Injectable } from '@angular/core';
import { FormField } from '../../../../../../../sellions_forms/projects/forms/src/lib/interfaces/form-field.interface';
import {
  FormFieldTechnicalParameters,
  ProcessFormMappingConfig,
} from '../../interfaces/process-form-mapping-config.interface';
import { Utils } from '../../utils/utils.class';

@Injectable()
export class ProcessFormService {
  public mapFormData(config: ProcessFormMappingConfig, fields: FormField[], data: any): FormField[] {
    const mapping = config.formMapping;
    if (!data || !mapping) {
      return fields;
    }

    fields.forEach((field: FormField) => {
      this.setPredefinedValue(config, field, data, mapping[field.id]);
    });
    return fields;
  }

  public appendFieldsFromMappingParentObjectIntoElement(
    $event: { process?: any; element?: Object },
    formMappingFromParentObject: { [key: string]: any },
    dataElement: Object,
  ) {
    let combinedData = dataElement;

    if ($event.element) {
      combinedData = { ...dataElement, ...$event.element };
    }

    if (formMappingFromParentObject && combinedData) {
      for (const key in formMappingFromParentObject) {
        const valueExpression = formMappingFromParentObject[key];
        let value = valueExpression;

        if (typeof valueExpression === 'string') {
          if (Utils.VARIABLE_REG_EXP.test(valueExpression)) {
            value = Utils.evaluateString(valueExpression, combinedData);
          } else if (combinedData.hasOwnProperty(valueExpression)) {
            value = combinedData[valueExpression];
          }
        } else if (Array.isArray(valueExpression)) {
          value = valueExpression.reduce(function(obj, prop) {
            return obj && obj[prop];
          }, combinedData);
        }

        combinedData[key] = value;
      }
    }
    $event.element = combinedData;
  }

  public setTechnicalParametersOnField(technicalParametersConfig: { [p: string]: FormFieldTechnicalParameters }, formJson: FormField[], data: Object) {
    if (technicalParametersConfig) {
      for(const fieldId in technicalParametersConfig) {
        if(fieldId.indexOf(".") === -1) {
          let formField = formJson.find(f => f.id === fieldId);
          if(formField) {
            formField.rowFormat = technicalParametersConfig[formField.id].referenceMapping ? JSON.parse(JSON.stringify(technicalParametersConfig[formField.id].referenceMapping)) : {};
            formField.dataSourceParams = technicalParametersConfig[formField.id].dataSourceParams ? JSON.parse(JSON.stringify(technicalParametersConfig[formField.id].dataSourceParams)) : {};
            if (formField.dataSourceParams && formField.dataSourceParams.httpParams && formField.dataSourceParams.httpParams.filters) {
              formField.dataSourceParams.httpParams.filters = Utils.evaluateString(formField.dataSourceParams.httpParams.filters, data);
            }
            if (technicalParametersConfig[formField.id].frontendSort) {
              formField.sort = technicalParametersConfig[formField.id].frontendSort;
            }
          }
        } else {
          let firstPathElement = fieldId.substring(0, fieldId.indexOf('.'));
          let restOfPath = fieldId.substring(fieldId.indexOf('.')+1);
          let subform = formJson.find(f => f.id === firstPathElement);
          if(subform && subform.subForm && subform.subForm.definition) {
            let newTechnicalParams = {};
            newTechnicalParams[restOfPath] = technicalParametersConfig[fieldId];
            this.setTechnicalParametersOnField(newTechnicalParams, subform.subForm.definition, data)
          }
        }
      }
    }
  }

  private setPredefinedValue(config: ProcessFormMappingConfig, field: FormField, data: any, fieldMapping: any) {
    if (!data) return;
    if (field.type === 'list') this.setPredefinedList(config, field, data, fieldMapping);
    else field.predefinedValue = this.getValue(config, data[fieldMapping], field);
  }

  private setPredefinedList(config: ProcessFormMappingConfig, field: FormField, data: any, fieldMapping: any) {
    let dataForField = data[fieldMapping];
    if (dataForField !== null && this.checkIfObjectOrArrayOfObjects(dataForField)) {
      let valueToSet = Array.isArray(dataForField) ? dataForField : [dataForField];
      this.setPredefinedObjectArray(config, field, valueToSet);
    } else if (field.subForm.definition.length == 1) {
      field.subForm.definition[0].predefinedValue = dataForField;
    } else {
      // flatten data_view [path] data -> subform
      this.setSubFormFields(fieldMapping, field, data);
    }
  }

  private checkIfObjectOrArrayOfObjects(variableToCheck: any) {
    return typeof variableToCheck === 'object' ||
      (Array.isArray(variableToCheck) && variableToCheck.length > 0 && typeof variableToCheck[0] === 'object')
  }

  private setSubFormFields(fieldMapping: any, field: FormField, data: any) {
    if (!fieldMapping) {
      console.debug('Mapping configuration is empty for a subform field with identifier=' + field.id);
      return;
    }
    const subFormMapping = Object.keys(fieldMapping).map((key) => [key, fieldMapping[key]]);
    for (let [subFormFieldName, flatDataPathFieldName] of subFormMapping) {
      this.setSubFormFieldsFromFlattenDataFields(field, subFormFieldName, data, flatDataPathFieldName);
    }
  }

  private setSubFormFieldsFromFlattenDataFields(formField: FormField, subFormFieldName, data: any, flatDataPathFieldName) {
    formField.subForm.definition.forEach((subFormField: FormField) => {
      if (subFormField.id === subFormFieldName) {
        subFormField.predefinedValue = data[flatDataPathFieldName];
      }
    });
  }

  private setPredefinedReferencesFromArrayValue(config: ProcessFormMappingConfig, value: any, field: FormField): string[] {
    let referenceFields: string[] = [];
    value.forEach((predefinedReference) => {
      const referenceField = this.setPredefinedReference(config, predefinedReference, field);
      referenceFields.push(referenceField);
    });

    return referenceFields;
  }

  private setPredefinedReference(config: ProcessFormMappingConfig, value: any, field: FormField): string {
    return value[config.referenceMapping[field.id]];
  }

  private setPredefinedObjectArray(config: ProcessFormMappingConfig, field: FormField, value: any[]) {
    field.predefinedValue = this.getObjectValues(config, value, field.subForm.definition);
  }

  private getObjectValues(config: ProcessFormMappingConfig, values: any, definition: FormField[]) {
    values.forEach((value: any) => {
      definition.forEach((field) => this.getValue(config, value, field));
      return value;
    });
    return values;
  }

  private getValue(config: ProcessFormMappingConfig, value: any, field: FormField): any {
    if (field.dataSource === 'dataView') {
      const predefinedReferencesInArray = Array.isArray(value);

      if (predefinedReferencesInArray) {
        return this.setPredefinedReferencesFromArrayValue(config, value, field);
      } else {
        return value;
      }
    }

    return value;
  }
}



// WEBPACK FOOTER //
// ./src/submodules/sellions_layout_renderer/projects/sellions-layout-renderer-lib/src/lib/services/process/process-form.service.ts